// Blog admin API surface. M5 — motiflink §5.2/§5.4.
//
// ⚠️ A POST IS SINGLE-LANGUAGE (owner ruling 2026-08-15, migration 0010).
// `languageCode` is the post's language and the ONLY bilingual mechanism: an
// Arabic post appears on /blog, an English one on /en/blog, never both. There
// is no per-post translation overlay and no <BilingualTabs> anywhere in this
// surface — write the article twice if you want it in both languages.
//
// Categories carry ONE shared name used by both locales.

import { api, apiUpload } from './api';

export type BlogLocale = 'ar' | 'en';

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  iconUrl: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BlogPostSummary {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featuredImage: string | null;
  youtubeUrl: string | null;
  languageCode: BlogLocale;
  isPublished: boolean;
  publishedAt: string | null;
  categoryId: string | null;
  categoryName: string | null;
  categorySlug: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BlogPost extends BlogPostSummary {
  content: string | null;
  authorId: string | null;
}

export interface BlogPostInput {
  title: string;
  slug: string;
  excerpt?: string | null;
  content?: string | null;
  featuredImage?: string | null;
  youtubeUrl?: string | null;
  categoryId?: string | null;
  languageCode?: BlogLocale;
  isPublished?: boolean;
}

// ─── Posts ──────────────────────────────────────────────────────────────────

export async function listPosts(
  params: { lang?: BlogLocale; published?: boolean; q?: string } = {},
  signal?: AbortSignal,
): Promise<{ posts: BlogPostSummary[]; total: number }> {
  const search = new URLSearchParams();
  if (params.lang) search.set('lang', params.lang);
  if (params.published !== undefined) search.set('published', String(params.published));
  if (params.q) search.set('q', params.q);
  search.set('limit', '100');

  const { data } = await api<{ data: { posts: BlogPostSummary[]; total: number } }>(
    `/admin/content/blog?${search.toString()}`,
    { signal },
  );
  return data;
}

export async function getPost(id: string, signal?: AbortSignal): Promise<BlogPost> {
  const { data } = await api<{ data: BlogPost }>(`/admin/content/blog/${id}`, { signal });
  return data;
}

export async function createPost(input: BlogPostInput): Promise<BlogPost> {
  const { data } = await api<{ data: BlogPost }>('/admin/content/blog', {
    method: 'POST',
    body: input,
  });
  return data;
}

export async function updatePost(id: string, input: Partial<BlogPostInput>): Promise<BlogPost> {
  const { data } = await api<{ data: BlogPost }>(`/admin/content/blog/${id}`, {
    method: 'PATCH',
    body: input,
  });
  return data;
}

export async function deletePost(id: string): Promise<void> {
  await api(`/admin/content/blog/${id}`, { method: 'DELETE' });
}

// ─── Categories ─────────────────────────────────────────────────────────────

export async function listCategories(signal?: AbortSignal): Promise<BlogCategory[]> {
  const { data } = await api<{ data: BlogCategory[] }>('/admin/content/blog-categories', {
    signal,
  });
  return data;
}

/**
 * One category. There is no GET /:id on the API (the list is small and always
 * cached), so this reads from the list rather than adding an endpoint nothing
 * else needs.
 */
export async function getCategory(id: string, signal?: AbortSignal): Promise<BlogCategory> {
  const all = await listCategories(signal);
  const found = all.find((c) => c.id === id);
  if (!found) throw new Error('Category not found');
  return found;
}

export async function createCategory(input: {
  name: string;
  slug?: string;
  iconUrl?: string | null;
}): Promise<BlogCategory> {
  const { data } = await api<{ data: BlogCategory }>('/admin/content/blog-categories', {
    method: 'POST',
    body: input,
  });
  return data;
}

export async function updateCategory(
  id: string,
  input: { name?: string; slug?: string; isActive?: boolean; iconUrl?: string | null },
): Promise<BlogCategory> {
  const { data } = await api<{ data: BlogCategory }>(`/admin/content/blog-categories/${id}`, {
    method: 'PATCH',
    body: input,
  });
  return data;
}

export async function deleteCategory(id: string): Promise<void> {
  await api(`/admin/content/blog-categories/${id}`, { method: 'DELETE' });
}

// ─── Images ─────────────────────────────────────────────────────────────────

/**
 * Upload-first: returns a CDN URL with NO DB row, so an unsaved post can carry
 * a featured image and TipTap can insert inline images mid-draft. Persisting
 * the URL is the caller's save.
 *
 * Routed through `apiUpload`, never a raw fetch — see the note there.
 */
export async function uploadBlogImage(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiUpload<{ data: { url: string } }>(
    '/admin/content/uploads/blog-image',
    form,
  );
  return data.url;
}
